import "./ViewLogChart.scss";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ReferenceArea,
} from "recharts";
import { faSearch, faHouse, faTable, faFileText } from "@fortawesome/free-solid-svg-icons";

const ViewLogChart = () => {
  const location = useLocation();
  const product = location.state?.product;
  const selectedLogFiles = location.state?.selectedLogFiles;
  const [LogDataLoading, setLogDataLoading] = useState(false);
  const [LogDataArray, setLogDataArray] = useState([]);
  const [selectedYAxisValues, setSelectedYAxisValues] = useState({}); // State for selected Y-axis values
  const [axisSelection, setAxisSelection] = useState({});
  const [showEvents, setShowEvents] = useState(false);
  const [showValueDots, setShowValueDots] = useState(false);
  const [leftAxisDomain, setLeftAxisDomain] = useState(["auto", "auto"]);
  const [rightAxisDomain, setRightAxisDomain] = useState(["auto", "auto"]);
  const [zoomRangeStart, setZoomRangeStart] = useState("dataMin");
  const [zoomRangeEnd, setZoomRangeEnd] = useState("dataMax");
  const [xAxisDomain, setXAxisDomain] = useState({start: "dataMin", end: "dataMax"});// Recharts X Axis domain

  const navigate = useNavigate();

  useEffect(() => {
    fetchLogData(); // Fetch data when the component mounts
  }, []); // Empty dependency array to run once when the component mounts

  const fetchLogData = async () => {
    try {
      setLogDataLoading(true);
      let tempLogDataArray = [];

      // Get the data for each log file
      for (let i = 0; i < selectedLogFiles.length; i++) {
        const response = await fetch(
          "https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod/logs/get/data/" +
            selectedLogFiles[i].id
        );
        if (response.ok) {
          const dataBody = await response.json();
          const data = dataBody.data;
          tempLogDataArray.push(...data);
        } else {
          console.error("Failed to fetch data");
        }
      }

      tempLogDataArray = tempLogDataArray.map((item, index) => ({ ...item, index }));
      setLogDataArray(tempLogDataArray); // Update the data
      setXAxisDomain({start: 'dataMin', end: 'dataMax'});
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLogDataLoading(false);
    }
  };

  // Function to handle 'View Log Data' button click
  const handleViewLogsDataClick = () => {
    navigate("/logs/data/" + product.id, {
      state: { product, selectedLogFiles },
    });
  };

  useEffect(() => {
    updateAxisDomain();
  }, [selectedYAxisValues, axisSelection]); // Dependencies array

  const handleMouseDown = (state) => {
    if (state && state.activePayload && state.activePayload.length > 0) {
      const dataIndex = state.activePayload[0].payload.index;
      setZoomRangeStart(dataIndex);
    }
    console.log("MouseDown: Zoom Start="+zoomRangeStart+" , Zoom End=" + zoomRangeEnd);
  };

  // New function to handle mouse up event on the chart
  const handleMouseUp = (state) => {
    if (state && state.activePayload && state.activePayload.length > 0) {
      const dataIndex = state.activePayload[0].payload.index;
      if (zoomRangeStart !== null && dataIndex >= zoomRangeStart) {
        setZoomRangeEnd(dataIndex);
      }
    }
    console.log("MouseUp: Zoom Start="+zoomRangeStart+" , Zoom End=" + zoomRangeEnd);
  };

  // Adjusted zoom in function
  const zoomIn = () => {
    if (zoomRangeStart !== null && zoomRangeEnd !== null) {
      console.log("ZoomIn: Zoom Start="+zoomRangeStart+" , Zoom End=" + zoomRangeEnd);
      setXAxisDomain({start: zoomRangeStart, end: zoomRangeEnd});
    }
    setZoomRangeStart("dataMin")
    setZoomRangeEnd("dataMax")
  };

  // Adjusted zoom out function
  const zoomOut = () => {
    setXAxisDomain({start: 'dataMin', end:'dataMax' });
    setZoomRangeStart("dataMin")
    setZoomRangeEnd("dataMax")
  };

  // Function to handle 'Logs Home' button click
  const handleNavigateLogsHome = (product) => {
    navigate("/logs/" + product.id, { state: { product } });
  };

  // Function to handle 'My Products' button click
  const handleNavigateHome = () => {
    navigate("/product");
  };

  // List of potential variables
  const potentialProperties = [
    "gen_status",
    "gen_freq",
    "gen_volt",
    "grid_status",
    "grid_freq",
    "grid_volt",
    "input_current",
    "input_va",
    "input_watt",
    "output_freq",
    "output_volt",
    "output_current",
    "output_va",
    "output_watt",
    "battery_volt",
    "battery_current",
    "external_current",
    "battery_temp",
    "transformer_temp",
    "busbar_temp",
    "heatsink_temp",
    "fan_speed",
    "mode",
    "error_code",
    "charge_stage",
  ];

  const propertyColors = {
    gen_status: "#FF6384", // Red
    gen_freq: "#FF7675", // Soft Red
    gen_volt: "#FF8C66", // Orange-Red
    grid_status: "#FFA500", // Orange
    grid_freq: "#FFC300", // Yellow-Orange
    grid_volt: "#FFD700", // Yellow
    input_current: "#DFFF00", // Yellow-Green
    input_va: "#9ACD32", // Yellowish Green
    input_watt: "#6B8E23", // Olive Green
    output_freq: "#3CB371", // Medium Sea Green
    output_volt: "#20B2AA", // Light Sea Green
    output_current: "#40E0D0", // Turquoise
    output_va: "#48D1CC", // Medium Turquoise
    output_watt: "#00CED1", // Dark Turquoise
    battery_volt: "#1E90FF", // Dodger Blue
    battery_current: "#4169E1", // Royal Blue
    external_current: "#0000FF", // Blue
    battery_temp: "#00008B", // Dark Blue
    transformer_temp: "#191970", // Midnight Blue
    busbar_temp: "#8A2BE2", // Blue Violet
    heatsink_temp: "#9932CC", // Dark Orchid
    fan_speed: "#9400D3", // Dark Violet
    mode: "#8B008B", // Dark Magenta
    error_code: "#800080", // Purple
    charge_stage: "#6A5ACD", // Slate Blue
  };

  const renderEventLines = (data) => {
    return data.map((entry, index) => {
      if (entry.event && entry.event.trim() !== "") {
        return (
          <ReferenceLine
            key={`event-${index}`}
            yAxisId="left"
            x={entry.index}
            stroke="grey"
            strokeWidth={4}
            ifLabel={false}
            payload={{ event: entry.event }} // Include event property in the payload
          />
        );
      }
      return null;
    });
  };

  const updateAxisDomain = () => {
    let newLeftMin = Infinity;
    let newLeftMax = -Infinity;
    let newRightMin = Infinity;
    let newRightMax = -Infinity;

    potentialProperties.forEach((property) => {
      if (selectedYAxisValues[property]) {
        LogDataArray.forEach((data) => {
          const value = data[property];
          if (value === null || value === undefined || !Number.isFinite(Number(value))) {
            return;
          }
          const numericValue = Number(value);
          const assignedAxis = axisSelection[property] || "left";
          if (assignedAxis === "left") {
            newLeftMin = Math.min(newLeftMin, numericValue);
            newLeftMax = Math.max(newLeftMax, numericValue);
          } else if (assignedAxis === "right") {
            newRightMin = Math.min(newRightMin, numericValue);
            newRightMax = Math.max(newRightMax, numericValue);
          }
        });
      }
    });

    setLeftAxisDomain(
      newLeftMin === Number.MAX_VALUE
        ? ["auto", "auto"]
        : [newLeftMin, newLeftMax]
    );

    setRightAxisDomain(
      newRightMin === Number.MAX_VALUE
        ? ["auto", "auto"]
        : [newRightMin, newRightMax]
    );
  };

  // This determines if the value is displayed or not
  const handleCheckboxChange = (property) => {
    setSelectedYAxisValues((prevState) => ({
      ...prevState,
      [property]: !prevState[property],
    }));
  };

  // This determines which Y-axis the values are shown on
  const handleAxisChange = (property, axis) => {
    console.log(property + "," + axis);
    setAxisSelection((prev) => ({
      ...prev,
      [property]: axis,
    }));
  };

  const handleShowEvents = (showevents) => {
    setShowEvents(!showevents);
  };

  // Handler for toggling value dots
  const handleToggleValueDots = () => {
    setShowValueDots(!showValueDots);
  };

  return (
    <div className="viewLogsChartContainer">
      <div className="viewLogsChartHeader">
        {LogDataLoading ? (
          <p>Loading...</p>
        ) : (
          <div className="viewLogsChartHeaderTitle">
            <div className="viewLogsChartProductType">
              {product.product_type}
            </div>
            <div className="viewLogsChartProductSerialNumber">
              {product.product_serial_number}
            </div>
          </div>
        )}
        <div className="viewLogsChartHeaderSearch">
          <div className="viewLogsChartHeaderSearchIcon">
            <FontAwesomeIcon icon={faSearch} className="searchIcon" />
          </div>
          <div className="viewLogsChartHeaderSearchInput">
            <input type="text" placeholder="Search/Filter..." />
          </div>
        </div>

        <div className="viewLogsChartFunctionButtons">
          <div className="viewLogsChartHeaderOperation">
            <button onClick={handleViewLogsDataClick}>
              <FontAwesomeIcon icon={faTable} />
              &nbsp; View Data
            </button>
          </div>
          <div className="viewLogDataNavigateLogsHome">
            <button className="navigationButton" onClick={() => handleNavigateLogsHome(product)} >
              <FontAwesomeIcon icon={faFileText} />&nbsp;Log Files
            </button>
          </div>
          <div className="viewLogsChartNavigateHome">
            <button className="navigationButton" onClick={() => handleNavigateHome()} >
              <FontAwesomeIcon icon={faHouse} />My Products
            </button>
          </div>
        </div>
      </div>
      {LogDataLoading ? (
        <p>Loading...</p>
      ) : (
        <div className="viewLogsChartContent">
          <div className="zpButtonCollection">
            <button className="zpButton" onClick={zoomIn}>
              Zoom In
            </button>
            <button className="zpButton" onClick={zoomOut}>
              Zoom Reset
            </button>
          </div>
          <div className="viewLogsChartScrollWrapper">
            <div className="viewLogsChart">
              <LineChart
                width={1200}
                height={650}
                data={LogDataArray}
                margin={{ top: 10, right: 30, left: 20, bottom: 5 }}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  allowDataOverflow
                  dataKey="index"
                  type="number"
                  domain={[xAxisDomain.start, xAxisDomain.end]}
                  tickFormatter={(index) => {
                    return LogDataArray[index]?.time ?? "";
                  }}
                  angle={-60} // Rotate the labels to -60 degrees
                  textAnchor="end" // Aligns the text towards the end of the anchor point
                  height={70} // Increase height to accommodate angled labels
                />
                <YAxis
                  yAxisId="left"
                  orientation="left"
                  domain={leftAxisDomain}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={rightAxisDomain}
                />
                {zoomRangeStart !== null && zoomRangeEnd !== null && (
                  <ReferenceArea
                    x1={zoomRangeStart}
                    x2={zoomRangeEnd}
                    yAxisId="left"
                    strokeOpacity={0.25}
                    stroke="red"
                    fill="lightpink"
                    fillOpacity={0.25}
                  />
                )}
                <Legend />
                <Tooltip content={<CustomTooltip />} />
                {potentialProperties.map(
                  (property) =>
                    selectedYAxisValues[property] && (
                      <Line
                        key={property}
                        yAxisId={axisSelection[property] || "left"}
                        type="monotone"
                        dataKey={property}
                        stroke={propertyColors[property] || "#8884d8"}
                        dot={showValueDots} // Added dot prop based on the new state
                        animationDuration={300}
                      />
                    )
                )}
                {showEvents && renderEventLines(LogDataArray)}
              </LineChart>
            </div>
            <div className="viewLogsChartChecklist">
              {potentialProperties.map((property) => (
                <div className="viewLogsChartChecklistContainer" key={property}>
                  <input
                    type="checkbox"
                    checked={selectedYAxisValues[property] || false}
                    onChange={() => handleCheckboxChange(property)}
                  />
                  <select
                    className="viewLogsChartAxisSelection"
                    onChange={(e) => handleAxisChange(property, e.target.value)}
                  >
                    <option value="left">Left Axis</option>
                    <option value="right">Right Axis</option>
                  </select>
                  <div style={{ color: propertyColors[property] }}>
                    {property}
                  </div>
                </div>
              ))}
              <div className="viewLogsChartShowEvents">
                <input
                  type="checkbox"
                  checked={showEvents || false}
                  onChange={() => handleShowEvents(showEvents)}
                />
                <span>Show Events</span>
              </div>
              <div className="viewLogsChartShowValueDots">
                <input
                  type="checkbox"
                  checked={showValueDots || false}
                  onChange={handleToggleValueDots}
                />
                <span>Show Value Dots</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {

    // Render tooltip content for event lines
    if (payload[0].payload.event !== "") {
      console.log("At event line")
      return (
        <div className="customTooltip">
          <p className="tooltipXValue">{`Time: ${payload[0].payload.date+" "+payload[0].payload.time}`}</p>
          <p className="tooltipEvent">{`${payload[0].payload.event}`}</p>
          {payload.map((p) => (
            <p className="tooltipValue" key={p.name} >
              {`${p.name}: ${p.value}`}
            </p>
          ))}
        </div>
      );
    }

    // Render tooltip content for data points
    return (
      <div className="customTooltip">
        <p className="tooltipXValue">{`Time: ${payload[0].payload.date+" "+payload[0].payload.time}`}</p>
        {payload.map((p) => (
          <p className="tooltipValue" key={p.name}>
            {`${p.name}: ${p.value}`}
          </p>
        ))}
      </div>
    );
  }

  return null;
};

const parseErrorCodeValues = (bitmask) => {

  const errorCodeValues = [
    "battery under voltage",      // 0000 0000 0000 0001
    "battery over voltage",       // 0000 0000 0000 0010
    "battery ultra low voltage",  // 0000 0000 0000 0100
    "input over current",         // 0000 0000 0000 1000

    "output over current",        // 0000 0000 0001 0000
    "output over load",           // 0000 0000 0010 0000
    "output short",               // 0000 0000 0100 0000
    "transformer over heat",      // 0000 0000 1000 0000

    "heat sink over heat 1",      // 0000 0001 0000 0000
    "output failure",             // 0000 0010 0000 0000
    "output over load 1",         // 0000 0100 0000 0000
    "output over load 2",         // 0000 1000 0000 0000

    "output over load 3",         // 0001 0000 0000 0000
    "output over load 4",         // 0010 0000 0000 0000
    "heat sink over heat 2",      // 0100 0000 0000 0000
    "stop output"                 // 1000 0000 0000 0000
  ]

  const statuses = [];
  let value = 0;
  let bitCheck;

  errorCodeValues.forEach((status, index) => {
    bitCheck = 1 << index;
    if ((bitmask & bitCheck) !== 0) {
      value += bitCheck;
      statuses.push(status);
    }
  });
  return statuses;
}


export default ViewLogChart;
